import { FC, useMemo } from "react";
import StarRate from "../../../components/StarRate";
import { IWidgetTemplate } from "../../../interfaces/widget-template";
import { ReachClick } from "../../../utils/core";
import { resolveWidgetV2Style } from "./widgetV2Styles";

/** Epsilon-guarded 1-decimal format, matches widget-v2.js's starsValueOf (applied here to an already 0-5 review score, not a 0-100 TrustScore). */
const formatStars = (value = 0): string => (Math.round(value * 10 + 1e-6) / 10).toFixed(1);

/**
 * Proof Nudge (v2) — admin-preview-only, shows the newest review as a static
 * snapshot. The real embed (widget-v2.js's mountNudge) instead rotates
 * through the 5 most recent reviews on a timer, fading a new one in every
 * 8-15s — not reproduced here since this page is a one-shot render, same as
 * every other widget-15..31 preview in this folder.
 */
const WidgetTemplate23: FC<IWidgetTemplate> = ({ company, review, style }) => {
  const companyId = company?.companyId;
  const resolvedStyle = resolveWidgetV2Style("nudge", style);
  const payload = useMemo(() => ({ companyId, widgetId: 23 as const }), [companyId]);
  const profileUrl = useMemo(
    () => `https://www.trustfinance.com/companies/${companyId}?utm_source=widget&utm_medium=dom&utm_campaign=widget-v2-nudge`,
    [companyId],
  );
  const first = review?.data?.[0];

  // No published reviews yet — the real embed never mounts in this case either.
  if (!first) return null;

  const name = first.user?.displayName ?? "A verified customer";
  const starsValue = formatStars(first.score);

  if (resolvedStyle === "inline-ticker") {
    return (
      <div className="d-flex justify-content-center">
        <a
          href={profileUrl}
          target="_blank"
          rel="noopener"
          onClick={() => ReachClick(payload)}
          className="d-flex align-items-center gap-2"
          style={{ textDecoration: "none", color: "#fff", background: "#1c1c2e", borderRadius: 8, padding: "6px 12px", fontSize: 11 }}
        >
          <StarRate value={first.score} size={10} />
          <span>{name} just left a {starsValue}-star review</span>
        </a>
      </div>
    );
  }

  // standard — Toast
  return (
    <div className="d-flex justify-content-center">
      <a
        href={profileUrl}
        target="_blank"
        rel="noopener"
        onClick={() => ReachClick(payload)}
        className="d-flex align-items-center gap-2"
        style={{
          textDecoration: "none",
          color: "#1c1c2e",
          border: "1px solid #e7e8ec",
          borderRadius: 8,
          background: "#fff",
          boxShadow: "0 8px 24px rgba(28,28,46,0.12)",
          padding: "10px 14px",
        }}
      >
        <StarRate value={first.score} size={11} />
        <span style={{ fontSize: 11, color: "#6b6b85" }}>{name} just left a {starsValue}-star review</span>
      </a>
    </div>
  );
};

export default WidgetTemplate23;
